import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { cx } from "../../utils/cx";
import Button from "./Button";

const WIDTHS = { sm: "max-w-md", md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-4xl", full: "max-w-6xl" };

// Open dialogs, oldest first: Escape and backdrop clicks belong to the one on top only, so a
// confirmation over another dialog does not close both
const openDialogs = [];
const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

export default function Modal({ open, onClose, title, description, footer, size = "md", dismissible = true, className, children }) {
  const self = useRef({});
  const isTop = () => openDialogs[openDialogs.length - 1] === self.current;
  const panel = useRef(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const dialog = self.current;
    openDialogs.push(dialog);
    return () => openDialogs.splice(openDialogs.indexOf(dialog), 1);
  }, [open]);

  // Escape closes the dialog on top, and the page behind stops scrolling while it is open
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && dismissible && isTop() && onClose?.();
    const previousOverflow = document.body.style.overflow;
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, dismissible, onClose]);

  // Focus moves into the dialog when it opens and back to what opened it when it closes
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    panel.current?.focus();
    return () => opener?.focus?.();
  }, [open]);

  // Tab stays inside the dialog on top
  const keepFocus = (e) => {
    if (e.key !== "Tab" || !isTop()) return;
    const focusable = [...panel.current.querySelectorAll(FOCUSABLE)].filter((el) => !el.disabled);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descriptionId : undefined}
      onKeyDown={keepFocus}
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => dismissible && isTop() && onClose?.()}
      />
      <div ref={panel} tabIndex={-1} className={cx("relative flex max-h-[90vh] w-full flex-col rounded-lg bg-white shadow-xl outline-none", WIDTHS[size], className)}>
        {(title || dismissible) && (
          <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-4">
            <div>
              {title && <h2 id={titleId} className="text-lg font-bold text-slate-900">{title}</h2>}
              {description && <p id={descriptionId} className="mt-1 text-sm text-slate-500">{description}</p>}
            </div>
            {dismissible && (
              <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        )}
        {children && <div className="thin-scrollbar min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>}
        {footer && <div className="flex justify-end gap-3 border-t border-slate-100 px-6 py-4">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title = "Are you sure?",
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  loading = false,
  children,
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="sm"
      dismissible={!loading}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={destructive ? "danger" : "solid"} size="sm" onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </Modal>
  );
}
